import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '@medikei/ui';
import { AuthLayout, AuthHeader, FormFooter } from '@/components/auth';
import { authApi, AuthApiError } from '@/api/http-client';
import { Mail, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      const error = err as Error;
      if (error instanceof AuthApiError) {
        if (error.code === 'USER_NOT_FOUND') {
          setError('Não encontramos uma conta com este email.');
        } else {
          setError(error.message || 'Erro ao processar solicitação');
        }
      } else {
        setError('Erro ao processar solicitação. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Email Enviado!
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Enviamos um link de recuperação para o email{' '}
            <strong>{email}</strong>.
            Verifique sua caixa de entrada e siga as instruções.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Não recebeu o email?{' '}
            <button
              onClick={() => setSuccess(false)}
              className="text-primary font-medium hover:text-primary/80"
            >
              Tentar novamente
            </button>
          </p>
          <Link to="/login">
            <Button variant="outline" className="w-full">
              <ArrowLeft size={16} className="mr-2" />
              Voltar para Login
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center mb-6">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Mail size={28} className="text-primary" />
        </div>
      </div>

      <AuthHeader
        title="Esqueceu sua senha?"
        description="Informe seu email e enviaremos um link para redefinir sua senha"
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          required
          autoComplete="email"
          autoFocus
        />

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
          loading={isLoading}
        >
          {isLoading ? 'Enviando...' : 'Enviar Link de Recuperação'}
        </Button>

        <FormFooter>
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Voltar para Login
          </Link>
        </FormFooter>
      </form>
    </AuthLayout>
  );
}